import { ChevronDown, Search } from "lucide-react";
import { useState } from "react";

const FILTER_OPTIONS = [
  { value: "TODAY", label: "Today" },
  { value: "THIS_WEEK", label: "This Week" },
  { value: "THIS_MONTH", label: "This Month" },
  { value: "LAST_MONTH", label: "Last Month" },
  { value: "THIS_YEAR", label: "This Year" },
  { value: "CUSTOM", label: "Custom Dates" },
];

/**
 * DateRangeFilter Component
 * Provides preset date filters with optional custom date range and filter button
 * Matches BusinessReports compact design
 * 
 * @param {string} selectedFilter - Currently selected filter value
 * @param {function} onFilterChange - Callback when filter changes
 * @param {object} customDates - { from: "YYYY-MM-DD", to: "YYYY-MM-DD" }
 * @param {function} onCustomDatesChange - Callback when custom dates change
 * @param {function} onApplyFilter - Callback when filter button is clicked
 */
export default function DateRangeFilter({
  selectedFilter = "TODAY",
  onFilterChange,
  customDates = { from: "", to: "" },
  onCustomDatesChange,
  onApplyFilter,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(selectedFilter === "CUSTOM");

  const handleFilterSelect = (value) => {
    setIsOpen(false);
    setShowCustom(value === "CUSTOM");
    onFilterChange(value);
  };

  const handleCustomDateChange = (type, value) => {
    const updated = { ...customDates, [type]: value };
    onCustomDatesChange(updated);
  };

  const isFilterDisabled = selectedFilter === "CUSTOM" && (!customDates.from || !customDates.to);

  const selectedLabel =
    FILTER_OPTIONS.find((opt) => opt.value === selectedFilter)?.label || "Select Filter";

  return (
    <div className="flex flex-col gap-3">
      {/* Row 1: Filter Dropdown and Apply Button */}
      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
        {/* Filter Dropdown */}
        <div className="relative flex-1 min-w-0">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <span className="truncate">{selectedLabel}</span>
            <ChevronDown
              className={`h-4 w-4 text-gray-500 flex-shrink-0 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Dropdown Menu */}
          {isOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-20 min-w-[160px]">
              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleFilterSelect(option.value)}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors text-sm font-medium border-l-2 ${
                    selectedFilter === option.value
                      ? "bg-blue-50 text-blue-600 border-blue-600"
                      : "text-gray-700 border-transparent"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Apply Filter Button */}
        <button
          onClick={onApplyFilter}
          disabled={isFilterDisabled}
          className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
            isFilterDisabled
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          <Search className="h-4 w-4" />
          <span className="hidden sm:inline">Apply</span>
        </button>
      </div>

      {/* Row 2: Custom Date Range Inputs (if CUSTOM selected) */}
      {showCustom && (
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={customDates.from}
              onChange={(e) => handleCustomDateChange("from", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={customDates.to}
              onChange={(e) => handleCustomDateChange("to", e.target.value)}
              min={customDates.from}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      )}
    </div>
  );
}
